import type { programs as ProgramsNS, sites as SitesNS } from '@cwos/api-client';
import {
  WITHHELD_BECAUSE,
  WITHHELD_LABEL,
  disclosedText,
  fromFlag,
  listFromFlag,
} from '@cwos/api-client';

/**
 * Pure presentation logic for the Programs and Sites screens.
 *
 * Everything here is a total function over plain data, so the rules a person
 * actually sees — what a status pill says, when a retire button is offered,
 * whether a form may be submitted, how a page of results is described — are
 * unit-tested without a browser, and the screens stay thin.
 */

export type StatusTone = 'good' | 'sky' | 'plain' | 'urgent';

export interface StatusBadge {
  variant: StatusTone;
  label: string;
  /** Plain-language consequence, so status is never colour-only. */
  meaning: string;
}

/**
 * The lifecycle vocabulary, shared by Programs and Sites.
 *
 * Retired is not a stronger "inactive": inactive means "paused, still ours to
 * use", retired means "out of service, kept for history". The `meaning` line
 * says so in words, so the distinction survives for anyone who cannot compare
 * two pill colours.
 */
export function statusBadge(status: string): StatusBadge {
  switch (status) {
    case 'active':
      return { variant: 'good', label: 'Active', meaning: 'In use for new work.' };
    case 'inactive':
      return {
        variant: 'sky',
        label: 'Inactive',
        meaning: 'Paused — not offered for new work, but not retired.',
      };
    case 'retired':
      return {
        variant: 'plain',
        label: 'Retired',
        meaning: 'Out of service. Past records still reference it.',
      };
    default:
      return { variant: 'plain', label: status, meaning: '' };
  }
}

/** A retired record is reactivated, never edited in place. */
export function canEdit(status: string): boolean {
  return status !== 'retired';
}

// --- Programs ---------------------------------------------------------------

export interface ProgramDraft {
  name: string;
  code: string;
  description: string;
  programLeadWorkerId: string;
  startsOn: string;
  endsOn: string;
  siteIds: string[];
  primarySiteId: string;
}

export const EMPTY_PROGRAM_DRAFT: ProgramDraft = {
  name: '',
  code: '',
  description: '',
  programLeadWorkerId: '',
  startsOn: '',
  endsOn: '',
  siteIds: [],
  primarySiteId: '',
};

export interface DraftErrors {
  [field: string]: string;
}

/**
 * Client-side validation, mirroring the server's zod contract.
 *
 * This exists to give immediate, field-level feedback — never as the
 * authority. The API validates the same rules again, and the database
 * constrains them a third time; a value that slipped past here is still
 * rejected there.
 */
export function validateProgramDraft(draft: ProgramDraft): DraftErrors {
  const errors: DraftErrors = {};
  const name = draft.name.trim();
  if (name.length === 0) errors['name'] = 'Give the program a name.';
  else if (name.length > 160) errors['name'] = 'Keep the name under 160 characters.';

  const code = draft.code.trim();
  if (code.length > 32) errors['code'] = 'Keep the short code under 32 characters.';
  else if (code.length > 0 && !/^[\w.-]+$/.test(code)) {
    errors['code'] = 'Use letters, numbers, dots, dashes or underscores.';
  }

  if (draft.description.length > 2000) {
    errors['description'] = 'Keep the description under 2000 characters.';
  }
  if (draft.startsOn && draft.endsOn && draft.endsOn < draft.startsOn) {
    errors['endsOn'] = 'The end date must be on or after the start date.';
  }
  if (draft.primarySiteId && !draft.siteIds.includes(draft.primarySiteId)) {
    errors['primarySiteId'] = 'The main site must be one of the selected sites.';
  }
  return errors;
}

/** Strip empties so an untouched optional field is sent as absent, not ''. */
export function programCreatePayload(draft: ProgramDraft): ProgramsNS.CreateProgramInput {
  return {
    name: draft.name.trim(),
    code: draft.code.trim() || null,
    description: draft.description.trim() || null,
    programLeadWorkerId: draft.programLeadWorkerId || null,
    startsOn: draft.startsOn || null,
    endsOn: draft.endsOn || null,
    siteIds: draft.siteIds.length > 0 ? draft.siteIds : undefined,
    primarySiteId: draft.primarySiteId || (draft.siteIds[0] ?? null),
  };
}

// --- Withheld facts, said out loud -------------------------------------------

/**
 * How a program's lead reads.
 *
 * `{program.programLeadName ?? 'Not set'}` is what this used to be, and "Not
 * set" is an affirmative claim: THIS PROGRAM HAS NO LEAD. For a caller without
 * `user:read` the server sends `programLeadName: null` because it is keeping
 * the name back, and the screen turned that into a statement that the program
 * is unowned — the exact inversion `programLeadNameWithheld`'s own comment in
 * the API client says must never appear.
 *
 * The flag is checked BEFORE the null, because a withheld lead is null too.
 */
export function programLeadLine(program: {
  programLeadName: string | null;
  programLeadNameWithheld: boolean;
}): string {
  return disclosedText(
    fromFlag(
      program.programLeadName,
      program.programLeadNameWithheld,
      'Not set',
      WITHHELD_BECAUSE.workerIdentity,
    ),
    (name) => name,
  );
}

/**
 * A cell in the program roster.
 *
 * `identityWithheld` covers five fields at once, and three of them —
 * `roleTitle`, `supervisorId`, `supervisorName` — are legitimately null on a
 * full row. So a redacted row rendered as `—` was indistinguishable from a real
 * row describing somebody with no job title and no supervisor, and a reader
 * concluded the second. The short form is used here because these are table
 * cells; the reason is stated once, above the table.
 */
export function rosterCell(
  worker: { identityWithheld: boolean },
  value: string | null,
  absentLabel: string,
): string {
  return worker.identityWithheld ? WITHHELD_LABEL : (value ?? absentLabel);
}

/**
 * The supervisor roll-up, which is names and nothing else.
 *
 * For an unentitled caller it arrives `[]` with `supervisorsWithheld: true`.
 * The screen rendered the list only when non-empty, so a withheld roll-up
 * disappeared entirely — reading as "nobody in this program reports to anyone".
 */
export function supervisorsLine(program: {
  supervisors: readonly unknown[];
  supervisorsWithheld: boolean;
}): string | null {
  const disclosed = listFromFlag(
    program.supervisors,
    program.supervisorsWithheld,
    '',
    WITHHELD_BECAUSE.workerIdentity,
  );
  // Nothing to say when the roll-up is genuinely empty — a program where nobody
  // supervises anybody is not withholding anything, and inventing a line for it
  // would be its own small untruth.
  return disclosed.state === 'withheld' ? disclosed.placeholder : null;
}

// --- Editing a program that already exists ----------------------------------

/**
 * The stored program, expressed as a form draft.
 *
 * Extracted so the three places that seed the form — first render, the effect
 * that re-seeds when the program reloads, and Cancel — cannot drift apart.
 */
export function programDraftOf(program: {
  name: string;
  code: string | null;
  description: string | null;
  programLeadWorkerId: string | null;
  startsOn: string | null;
  endsOn: string | null;
}): ProgramDraft {
  return {
    ...EMPTY_PROGRAM_DRAFT,
    name: program.name,
    code: program.code ?? '',
    description: program.description ?? '',
    programLeadWorkerId: program.programLeadWorkerId ?? '',
    startsOn: program.startsOn ?? '',
    endsOn: program.endsOn ?? '',
  };
}

/** What one draft field would be on the wire. */
const wireText = (value: string): string | null => value.trim() || null;

/**
 * ONLY THE FIELDS THIS PERSON ACTUALLY CHANGED.
 *
 * This function exists because of a data-loss defect, and the defect is worth
 * stating precisely, because the naive fix does not close it.
 *
 * The screen used to send every field on every save:
 *
 *     programLeadWorkerId: draft.programLeadWorkerId || null
 *
 * unconditionally. For an administrator holding `program:manage` but NOT
 * `user:read`, the server withholds the program's lead — `programLeadName` and
 * `programLeadWorkerId` both arrive `null` — so the draft seeded to `''` and
 * every save transmitted `programLeadWorkerId: null`. The API writes the column
 * for any patch that carries the key. Editing a program's DESCRIPTION therefore
 * deleted its lead: a fact the editor had never been permitted to see, removed
 * without being shown to them, and without the field ever appearing dirty.
 *
 * WHY NOT "never send a key you did not receive". Because the key WAS received.
 * It arrived as `programLeadWorkerId: null`, which is exactly what an unled
 * program sends to a caller entitled to see it. The two are the same key with
 * the same value; only `programLeadNameWithheld` separates them, and it is
 * `false` for a withheld caller looking at a program that has no lead. A rule
 * built on key presence cannot see this.
 *
 * WHY A DIFF. Because the honest thing to submit is what the person changed,
 * and a value they were never shown is a value they cannot have changed. That
 * needs no knowledge of permissions and no withholding flag, and it fixes the
 * whole class rather than this one field: any future column the server decides
 * to withhold is already safe here.
 *
 * The screen ALREADY KNEW. It computed a per-field `dirty` comparison to decide
 * whether to enable the Save button — and that comparison correctly reported
 * the lead unchanged. Then the payload builder threw the answer away and sent
 * everything. So `programDraftDirty` below is now derived from this function,
 * which makes it impossible for the button and the request to disagree again.
 *
 * This is not a control. The API is the boundary, and it now refuses a write to
 * `programLeadWorkerId` from a caller who may not read it (403 —
 * `withheldFieldsWritten` in the programs routes). A second client, a script or
 * a replayed request is stopped there. This is the client being correct.
 */
export function programUpdatePayload(
  draft: ProgramDraft,
  program: {
    name: string;
    code: string | null;
    description: string | null;
    programLeadWorkerId: string | null;
    startsOn: string | null;
    endsOn: string | null;
  },
): ProgramsNS.UpdateProgramInput {
  const payload: ProgramsNS.UpdateProgramInput = {};
  // Compared on the WIRE value, not the draft string: typing a trailing space
  // into the name and deleting it again is not an edit, and offering to save it
  // would send a patch the server answers with "at least one field".
  if (draft.name.trim() !== program.name) payload.name = draft.name.trim();
  if (wireText(draft.code) !== program.code) payload.code = wireText(draft.code);
  if (wireText(draft.description) !== program.description) {
    payload.description = wireText(draft.description);
  }
  if ((draft.programLeadWorkerId || null) !== program.programLeadWorkerId) {
    payload.programLeadWorkerId = draft.programLeadWorkerId || null;
  }
  if ((draft.startsOn || null) !== program.startsOn) payload.startsOn = draft.startsOn || null;
  if ((draft.endsOn || null) !== program.endsOn) payload.endsOn = draft.endsOn || null;
  return payload;
}

/**
 * Is there anything to save? Derived from the payload itself, so the Save button
 * and the request it sends can never disagree about what changed.
 */
export function programDraftDirty(
  draft: ProgramDraft,
  program: Parameters<typeof programUpdatePayload>[1],
): boolean {
  return Object.keys(programUpdatePayload(draft, program)).length > 0;
}

/** A one-line answer to "where does this program operate from?". */
export function programSitesLabel(program: {
  primarySiteName: string | null;
  siteCount: number;
}): string {
  if (program.siteCount === 0) return 'No fixed site';
  if (!program.primarySiteName) {
    return program.siteCount === 1 ? '1 site' : `${program.siteCount} sites`;
  }
  const others = program.siteCount - 1;
  return others > 0 ? `${program.primarySiteName} +${others} more` : program.primarySiteName;
}

/** The current members of a program: assignments that have not ended. */
export function currentWorkers(
  workers: readonly ProgramsNS.ProgramWorker[],
): ProgramsNS.ProgramWorker[] {
  return workers.filter((w) => w.effectiveTo == null);
}

/** Past members, most recently departed first — the program's history tab. */
export function pastWorkers(
  workers: readonly ProgramsNS.ProgramWorker[],
): ProgramsNS.ProgramWorker[] {
  return workers
    .filter((w) => w.effectiveTo != null)
    .sort((a, b) => (b.effectiveTo ?? '').localeCompare(a.effectiveTo ?? ''));
}

// --- Sites ------------------------------------------------------------------

export interface SiteDraft {
  name: string;
  code: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  timezone: string;
  notes: string;
  latitude: number | null;
  longitude: number | null;
  coordinateSource: string;
  resolvedAddress: string | null;
  programIds: string[];
  geofenceEnabled: boolean;
  geofenceRadiusMeters: number;
}

export const EMPTY_SITE_DRAFT: SiteDraft = {
  name: '',
  code: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postalCode: '',
  country: '',
  timezone: '',
  notes: '',
  latitude: null,
  longitude: null,
  coordinateSource: 'unknown',
  resolvedAddress: null,
  programIds: [],
  geofenceEnabled: false,
  geofenceRadiusMeters: 150,
};

export function validateSiteDraft(draft: SiteDraft): DraftErrors {
  const errors: DraftErrors = {};
  const name = draft.name.trim();
  if (name.length === 0) errors['name'] = 'Give the site a name.';
  else if (name.length > 160) errors['name'] = 'Keep the name under 160 characters.';

  const code = draft.code.trim();
  if (code.length > 32) errors['code'] = 'Keep the short code under 32 characters.';
  else if (code.length > 0 && !/^[\w.-]+$/.test(code)) {
    errors['code'] = 'Use letters, numbers, dots, dashes or underscores.';
  }

  if (draft.latitude != null && (draft.latitude < -90 || draft.latitude > 90)) {
    errors['latitude'] = 'Latitude must be between −90 and 90.';
  }
  if (draft.longitude != null && (draft.longitude < -180 || draft.longitude > 180)) {
    errors['longitude'] = 'Longitude must be between −180 and 180.';
  }
  if (draft.geofenceEnabled) {
    if (draft.latitude == null || draft.longitude == null) {
      // The server refuses this too; saying it here avoids a round trip that
      // ends in a 409 the person cannot connect to the toggle they flipped.
      errors['geofence'] =
        'A geofence needs the site’s position. Find the address on the map first.';
    } else if (draft.geofenceRadiusMeters < 1 || draft.geofenceRadiusMeters > 50_000) {
      errors['geofence'] = 'The radius must be between 1 metre and 50 km.';
    }
  }
  return errors;
}

export function siteCreatePayload(draft: SiteDraft): SitesNS.CreateSiteInput {
  const hasCoordinates = draft.latitude != null && draft.longitude != null;
  return {
    name: draft.name.trim(),
    code: draft.code.trim() || null,
    addressLine1: draft.addressLine1.trim() || null,
    addressLine2: draft.addressLine2.trim() || null,
    city: draft.city.trim() || null,
    region: draft.region.trim() || null,
    postalCode: draft.postalCode.trim() || null,
    country: draft.country.trim() || null,
    timezone: draft.timezone.trim() || null,
    notes: draft.notes.trim() || null,
    coordinates: hasCoordinates
      ? {
          latitude: draft.latitude!,
          longitude: draft.longitude!,
          // 'imported'/'unknown' are never claimed by a request; a coordinate a
          // person put here is either geocoded or placed by hand.
          source: draft.coordinateSource === 'geocoded' ? 'geocoded' : 'manual',
          resolvedAddress: draft.resolvedAddress,
        }
      : null,
    programIds: draft.programIds.length > 0 ? draft.programIds : undefined,
    geofence: draft.geofenceEnabled
      ? { enabled: true, radiusMeters: draft.geofenceRadiusMeters }
      : undefined,
  };
}

/** How the geofence column reads in a list. Never "off" when it is misconfigured. */
export function geofenceLabel(site: {
  geofence: { radiusMeters: number; status: string } | null;
  latitude: number | null;
}): string {
  if (!site.geofence) return site.latitude == null ? 'No position' : 'Not set';
  if (site.geofence.status !== 'active') return 'Switched off';
  return `${site.geofence.radiusMeters} m`;
}

// --- Shared list helpers ----------------------------------------------------

/** "1–25 of 240" — a person needs to know a list is a page, not the whole set. */
export function rangeLabel(total: number, limit: number, offset: number, shown: number): string {
  if (total === 0) return 'No results';
  const first = offset + 1;
  const last = offset + shown;
  return total <= shown && offset === 0 ? `${total} total` : `${first}–${last} of ${total}`;
}

export function hasNextPage(total: number, limit: number, offset: number): boolean {
  return offset + limit < total;
}

export function hasPreviousPage(offset: number): boolean {
  return offset > 0;
}

/**
 * Why a list is empty, in the caller's own terms.
 *
 * A brand-new organization and a search that matched nothing are completely
 * different situations, and telling them apart is the difference between "here
 * is how to start" and "your filter is too narrow".
 */
export type EmptyReason = 'no_records' | 'no_matches';

export function emptyReason(query: string, statusFilter: string): EmptyReason {
  return query.trim().length > 0 || statusFilter !== 'default' ? 'no_matches' : 'no_records';
}

/**
 * The per-installation freshness pill on the Organization page.
 *
 * It used to read `{s.degraded ? 'Reconnecting' : 'Healthy'}`. `degraded`
 * on `IntegrationHealth.syncs[]` means "not delivering fresh, complete data
 * right now" (dashboard/service.ts) — true for a delayed source, a source
 * whose historical import is still running, and one whose reconciliation
 * came up short. Nobody is reconnecting in any of those; the word named an
 * activity that was not happening. Each label below is the positive fact
 * the two wire fields actually state, and nothing more.
 */
export function integrationSyncPill(s: {
  degraded: boolean;
  staleness: 'current' | 'delayed' | 'stale' | 'unknown';
}): { label: string; variant: 'good' | 'look' | 'urgent' | 'plain' } {
  if (!s.degraded) return { label: 'Current', variant: 'good' };
  switch (s.staleness) {
    case 'stale':
      return { label: 'Updates have stopped', variant: 'urgent' };
    case 'delayed':
      return { label: 'Running behind', variant: 'look' };
    case 'current':
    case 'unknown':
    default:
      return { label: 'Not fully current', variant: 'look' };
  }
}
